'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { GeoforgeLogoWithText } from '@/components/icons/geoforge-logo-with-text';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useUrlWithReturnTo } from '@/hooks/use-url-with-return-to';
import { signIn } from '@/app/actions/auth';
import { signInWithSso } from '@/app/actions/sso';
import { APP_SETTINGS } from '@/lib/config';
import { initializeUserInStore } from '@/lib/init-user-utils';
import {
  PROVIDER_REFUSAL_ERROR,
  PROVIDER_REFUSAL_MESSAGE,
  callbackErrorMessage,
  parseCallbackErrorHash,
} from '@/utils/auth/callback-error-hash';
import { CAPTCHA_FAILED_MESSAGE } from '@/utils/auth/captcha';
import {
  SSO_REDIRECT_NOTICE,
  SSO_REQUIRED_ERROR_CODE,
  SSO_REQUIRED_MESSAGE,
  SSO_START_FAILED_MESSAGE,
  parseSsoSlugTag,
} from '@/utils/auth/sso-copy';
import { getSafeRedirectPath } from '../../utils/redirect-validation';
import { AuthDivider } from './auth-divider';
import { CaptchaWidget, useCaptcha } from './captcha';
import { GoogleAuthButton } from './google-auth-button';
import { LoginSsoStep } from './login-sso-step';
import { PasswordInput } from './password-input';

/** Messages for the `error` the auth routes put on a redirect to this page. */
const LINK_ERROR_MESSAGES = new Map(
  Object.entries({
    auth_callback_failed:
      'Password reset link is invalid or has expired. Please request a new one.',
    confirmation_link_invalid:
      "This confirmation link is invalid or has expired. Sign in if you've already confirmed, or sign up again for a new link.",
    missing_auth_code: 'Invalid password reset link. Please check your email and try again.',
    sign_in_failed: "We couldn't complete that sign-in. Please try again.",
    [PROVIDER_REFUSAL_ERROR]: PROVIDER_REFUSAL_MESSAGE,
    // A refusal whose company name did not survive, so there is nowhere to send them: say why.
    sso_required: 'Your organization requires single sign-on. Use Sign in with SSO to continue.',
  })
);

const LoginForm = () => {
  const [error, setError] = useState<string | null>(null);
  // In the card, not a toast: someone arriving from an emailed link must not miss why they are here.
  const [linkError, setLinkError] = useState<string | null>(null);
  const router = useRouter();
  const searchParams = useSearchParams();
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingText, setLoadingText] = useState<string>('Loading...');
  const { buildUrlWithReturnToParam } = useUrlWithReturnTo();
  const emailParam = searchParams.get('email');
  // Controlled so whatever has been typed can travel to the SSO step as a hint.
  const [email, setEmail] = useState(emailParam ?? '');

  const slugParam = searchParams.get('sso');
  const wasRefusedBySso = searchParams.get('reason') === 'sso_required';
  const [ssoStep, setSsoStep] = useState<{ slug: string; autoStart: boolean } | null>(
    slugParam ? { slug: slugParam, autoStart: wasRefusedBySso } : null
  );
  const [ssoNotice, setSsoNotice] = useState<string | null>(null);
  const [ssoRefusalSlug, setSsoRefusalSlug] = useState<string | null>(null);
  const { widgetRef, attachCaptchaToken, resetCaptcha } = useCaptcha();

  useEffect(() => {
    // The fragment wins: when it carries a reason, the query param is the callback route's
    // fallback guess and naming a "password reset link" would be actively wrong.
    const hashError = parseCallbackErrorHash(window.location.hash);
    if (hashError) {
      // Developer-facing: this is where a failing auth hook names itself.
      console.error('Auth callback failed:', hashError.code, hashError.description);
      // Drop the fragment so a refresh does not replay the message, and the stale `error` the
      // callback route guessed — leaving it in the URL contradicts the message just shown.
      const cleaned = new URLSearchParams(window.location.search);
      cleaned.delete('error');
      const query = cleaned.toString();
      window.history.replaceState(null, '', window.location.pathname + (query ? `?${query}` : ''));
    }

    const message = hashError
      ? callbackErrorMessage(hashError)
      : LINK_ERROR_MESSAGES.get(searchParams.get('error') ?? '');
    // Set, never cleared, here: cleaning the URL above re-runs this with neither source left.
    if (message) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- the fragment exists only in the browser, after hydration
      setLinkError(message);
    }
  }, [searchParams]);

  const startSso = async (options: { email?: string; slug?: string }) => {
    setSsoNotice(SSO_REDIRECT_NOTICE);
    const { url } = await signInWithSso({
      ...options,
      redirectTo: getSafeRedirectPath(searchParams.get('returnTo')),
    });

    if (url) {
      window.location.href = url;
      return;
    }

    setSsoNotice(null);
    setError(SSO_START_FAILED_MESSAGE);
    setLoading(false);
    setLoadingText('Loading...');
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    setLoadingText('Signing in...');
    setError(null);
    setLinkError(null);
    setSsoRefusalSlug(null);
    const formData = new FormData(event.currentTarget);

    if (!(await attachCaptchaToken(formData))) {
      setError(CAPTCHA_FAILED_MESSAGE);
      setLoading(false);
      setLoadingText('Loading...');
      return;
    }

    const result = await signIn(formData);

    // signIn checks the address before the password, so a routed one comes back here without ever
    // having been authenticated. Recovery accounts and ordinary domains fall straight through.
    if (result.error?.code === SSO_REQUIRED_ERROR_CODE) {
      await startSso({ email });
      return;
    }

    if (result.isSuccess) {
      setError(null);
      await initializeUserInStore();
      setLoadingText('Redirecting...');

      const returnTo = searchParams.get('returnTo');
      const redirectPath = getSafeRedirectPath(returnTo);
      router.push(redirectPath);
    } else {
      resetCaptcha();
      setLoading(false);
      setLoadingText('Loading...');

      // Someone the address check did not catch: the recovery list changed under a request, or a
      // caller went straight to the auth API. Anomalous enough to offer a button, not a redirect.
      const refusedSlug = parseSsoSlugTag(result.error?.message);
      if (refusedSlug) {
        setSsoRefusalSlug(refusedSlug);
        setError(SSO_REQUIRED_MESSAGE);
        return;
      }

      setError(result.error?.message || 'An error occurred');
    }
  };

  return (
    <div className="mx-auto w-full max-w-md rounded-md border border-gray-25 p-[25px]">
      <div className="mb-6 flex justify-center">
        <GeoforgeLogoWithText />
      </div>

      <div className="mb-6 text-center">
        <h1 className="mb-4 text-xl font-bold text-black">Sign In</h1>
        <p className="text-sm text-blue-25">
          {ssoStep
            ? 'Enter your company name to continue with single sign-on'
            : 'Welcome! Please sign in to continue'}
        </p>
      </div>

      {linkError && (
        <p
          role="alert"
          className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-center text-sm text-[#B91C1C]"
        >
          {linkError}
        </p>
      )}

      {ssoStep ? (
        <LoginSsoStep
          initialSlug={ssoStep.slug}
          autoStart={ssoStep.autoStart}
          email={email || undefined}
          onBack={() => setSsoStep(null)}
        />
      ) : (
        <>
          {/* POST keeps credentials out of the URL on a pre-hydration submit. */}
          <form method="post" onSubmit={handleSubmit} className="flex flex-col gap-2">
            <div className="space-y-2">
              <Label htmlFor="email">Email address</Label>
              <Input
                className="mt-1.5"
                type="email"
                placeholder="user@example.com"
                id="email"
                name="email"
                value={email}
                onChange={event => setEmail(event.target.value)}
                required
              />
            </div>

            <PasswordInput
              label="Password"
              name="password"
              id="password"
              placeholder="Password"
              required
            />

            {error && <p className="text-center text-sm text-red-500">{error}</p>}
            {ssoNotice && <p className="text-center text-sm text-blue-25">{ssoNotice}</p>}
            {ssoRefusalSlug && !ssoNotice && (
              <Button
                type="button"
                variant="outline"
                className="mt-2 w-full"
                onClick={() => startSso({ slug: ssoRefusalSlug, email: email || undefined })}
              >
                Continue with your company login
              </Button>
            )}

            <div className="text-right">
              <Link
                href={buildUrlWithReturnToParam('/forgot-password')}
                className="text-sm text-blue-500 hover:text-blue-700"
              >
                Forgot your password?
              </Link>
            </div>

            <CaptchaWidget ref={widgetRef} />

            <Button
              type="submit"
              isLoading={loading}
              loadingText={loadingText}
              className="mt-2 w-full bg-black"
            >
              Sign In
            </Button>
          </form>

          <AuthDivider />
          {APP_SETTINGS.ENABLE_GOOGLE_AUTH && <GoogleAuthButton mode="signin" />}
          <Button
            type="button"
            variant="outline"
            className={APP_SETTINGS.ENABLE_GOOGLE_AUTH ? 'mt-2 w-full' : 'w-full'}
            onClick={() => setSsoStep({ slug: '', autoStart: false })}
          >
            Sign in with SSO
          </Button>
        </>
      )}

      <div className="mt-6 text-center text-sm text-defaultFont">
        <span>Don&apos;t have an account? </span>
        <Link
          href={buildUrlWithReturnToParam('/signup')}
          className="font-bold text-defaultFont hover:text-defaultFont/80"
        >
          Sign up
        </Link>
        <span> for free.</span>
      </div>
    </div>
  );
};

export default LoginForm;
